import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  COMPONENTS_PAGE_BOTTOM,
  COMPONENTS_PAGE_INSET,
  COMPONENTS_PAGE_RAIL,
  COMPONENTS_PAGE_TOP,
} from '@/components/smartai/ComponentsList/components-page-layout';

/** Same content rail as Components list — matches header breadcrumb gutters. */
export const onboardingPageClass = cn(
  COMPONENTS_PAGE_RAIL,
  COMPONENTS_PAGE_INSET,
  COMPONENTS_PAGE_TOP,
  COMPONENTS_PAGE_BOTTOM,
  'min-h-[calc(100vh-var(--workspace-header-height,2.75rem))] bg-[var(--inv-bg)]',
);

export const formLabelClass =
  'mb-1.5 block text-[0.8125rem] font-medium leading-none tracking-wide text-[var(--inv-text-secondary)]';

export const fieldErrorClass = 'mt-1.5 text-xs leading-5 text-[#b42318]';

/** Shared “Change platform” control — same look on GitHub + Azure onboarding. */
export function ChangePlatformButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="mb-4">
      <Button
        variant="ghost"
        type="button"
        className="h-auto gap-1.5 border-none px-0 text-[var(--inv-accent)] hover:cursor-pointer hover:bg-transparent hover:text-[var(--inv-accent-hover)]"
        onClick={onClick}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Change platform
      </Button>
    </div>
  );
}

interface OnboardingPageProps {
  children: ReactNode;
  className?: string;
}

export function OnboardingPage({ children, className }: OnboardingPageProps) {
  return <div className={cn(onboardingPageClass, className)}>{children}</div>;
}

interface OnboardCardProps {
  children: ReactNode;
  className?: string;
  secondary?: boolean;
}

export function OnboardCard({
  children,
  className,
  secondary = false,
}: OnboardCardProps) {
  return (
    <Card
      className={cn(
        'box-border mb-5 w-full min-w-0 max-w-full border border-solid border-[var(--inv-border)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]',
        secondary && 'p-5',
        className,
      )}>
      {children}
    </Card>
  );
}

interface OnboardCardHeaderProps {
  title: string;
  action?: ReactNode;
}

export function OnboardCardHeader({ title, action }: OnboardCardHeaderProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-t-[14px] bg-[var(--inv-surface-muted)] px-6 py-4">
      <span className="text-base font-semibold text-[var(--inv-text)]">{title}</span>
      {action}
    </div>
  );
}

export function OnboardCardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <CardContent className={cn('p-5 px-6', className)}>{children}</CardContent>;
}

interface FormFieldProps {
  label: string;
  children: ReactNode;
  error?: string;
  layout?: 'default' | 'pat';
  actionRow?: ReactNode;
  loading?: ReactNode;
}

export function FormField({
  label,
  children,
  error,
  layout = 'default',
  actionRow,
  loading,
}: FormFieldProps) {
  /* `pat` layout: label, then input+action on one row, error below — keeps button level with input. */
  if (layout === 'pat') {
    return (
      <div className="box-border min-w-0 w-full max-w-full">
        <label className={formLabelClass}>{label}</label>
        <div className="flex items-center gap-3">
          <div className="box-border min-w-0 flex-1">{children}</div>
          {actionRow ? (
            <div className="box-border flex h-10 shrink-0 items-center">
              {actionRow}
            </div>
          ) : null}
        </div>
        {error ? (
          <p role="alert" className={fieldErrorClass}>
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mb-6">
      <label className={formLabelClass}>{label}</label>
      {actionRow || loading ? (
        <div className="flex items-center gap-5">
          <div className="min-w-0 flex-1">{children}</div>
          {loading}
          {actionRow}
        </div>
      ) : (
        children
      )}
      {error ? (
        <p role="alert" className={fieldErrorClass}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function FormRow({
  cols = 2,
  children,
  className,
}: {
  cols?: 2 | 3;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid items-start gap-5',
        cols === 2 && 'grid-cols-1 md:grid-cols-2',
        cols === 3 && 'grid-cols-1 md:grid-cols-3',
        '[&>div]:mb-0',
        className,
      )}>
      {children}
    </div>
  );
}

export function PatRow({
  children,
  action,
}: {
  children: ReactNode;
  action: ReactNode;
}) {
  return (
    <div className="mb-6 grid w-full grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div className="box-border min-w-0 max-w-full">{children}</div>
      {/* Offset by label height so the button lines up with the input, not the error. */}
      <div className="box-border flex h-10 shrink-0 items-center sm:mt-[1.1875rem]">
        {action}
      </div>
    </div>
  );
}

export function CascadeSection({
  locked,
  children,
}: {
  locked?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-6',
        locked && 'pointer-events-none opacity-85',
      )}>
      {children}
    </div>
  );
}

export function SectionDivider({ label }: { label: string }) {
  return (
    <div className="my-8 mb-6 flex items-center gap-3">
      <span className="h-px flex-1 bg-[var(--inv-border)]" />
      <span className="whitespace-nowrap text-[0.8125rem] font-semibold uppercase tracking-wider text-[var(--inv-text-muted)]">
        {label}
      </span>
      <span className="h-px flex-1 bg-[var(--inv-border)]" />
    </div>
  );
}

export function ApiErrorAlert({ message }: { message?: string | null }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="mt-6 rounded-[10px] border border-solid border-[#fecdca] bg-[#fef3f2] px-3.5 py-2.5 text-sm text-[#b42318]">
      {message}
    </div>
  );
}

export function InfoAlert({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-6 rounded-[10px] border-l-4 bg-[var(--inv-accent-soft)] px-5 py-4 text-[var(--inv-accent-hover)]',
        className,
      )}>
      {children}
    </div>
  );
}

export function FooterButtons({
  left,
  right,
}: {
  left?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-0 border-t border-solid border-[var(--inv-border-soft)] pt-5">
      <div className="flex flex-wrap items-center gap-3">{left}</div>
      {right ? <div className="flex flex-wrap items-center gap-3">{right}</div> : null}
    </div>
  );
}

export function TechStackSection({ children }: { children: ReactNode }) {
  return (
    <div className="mt-8 pt-6">
      {children}
    </div>
  );
}

export function TestSection({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6">{children}</div>;
}

export function TestScriptLoadSection({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex flex-col gap-6">{children}</div>;
}

export function ProjectMetaHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'mb-5 flex flex-wrap items-center gap-x-5 gap-y-2.5 rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-3 text-sm text-[var(--inv-text-secondary)] [&_strong]:font-semibold [&_strong]:text-[var(--inv-text)]',
        className,
      )}>
      {children}
    </div>
  );
}
